import React from "react"
import styled from "styled-components"

import { Exchange, TradingPair } from "../../reducer/common.types"
import { Primitive } from "../../reducer/common.types"
import { NbaTeam } from "../../reducer/nba.types"
import { BottomMarginText } from "../../styles"

interface DropDownProps<T extends Primitive> {
  /** Callback that gets invoked on value change */
  onChange: (state: T) => void
  /** Is this dropdown editable? We don't want this if we're not
   * connect to a socket API
   */
  editable: boolean
  /** Title displayed above the dropdown */
  title: string
  /** The current value of the dropdown */
  value: T
  /** The list of valid values for the dropdown */
  alternatives: readonly T[]
}

/** Wrapper for inputs such as dropdowns and text fields */
const InputDiv = styled.div`
  display: flex;
  flex-direction: column;
`

/** Capitalizes the given string */
const capitalize = (str: string): string => {
  if (str.length === 0) {
    return ""
  } else {
    return str.slice(0, 1).toUpperCase() + str.slice(1)
  }
}

/**
 * Parses the value of the given event, depending on
 * what sort of value our dropdown is dealing with.
 */
function maybeParse<T extends { toString: () => string }>(
  value: T,
  event: React.ChangeEvent<HTMLSelectElement>
): typeof value {
  if (typeof value === "number") {
    return Number.parseInt(event.target.value, 10) as any // hm...
  } else {
    // trading pairs should be kept as upper case
    // team IDs should be kept as upper case
    if (TradingPair.isTradingPair(value) || NbaTeam.isNbaTeam(value)) {
      return event.target.value as any // hm...
    } else {
      // everything else is lower case
      return event.target.value.toString().toLowerCase() as any // hm...
    }
  }
}
/** Component that lets the user choose between a
 * defined set of alternatives.
 */
export function DropDown<T extends Primitive>({
  alternatives,
  onChange,
  title,
  editable,
  value
}: DropDownProps<T>): JSX.Element {
  return (
    <InputDiv>
      <BottomMarginText>{title}</BottomMarginText>
      <select
        onChange={evt => {
          const parsed = maybeParse(value, evt)
          onChange(parsed)
        }}
      >
        {alternatives.map(a => {
          const description =
            Exchange.isExchange(a) && a === "krakenfut" ? "Kraken (futures)" : a
          return (
            <option
              disabled={!editable}
              key={a.toString()}
              // we get a warning for doing it this way,
              // but I don't know how to pre-select
              // an element without using `selected`
              selected={a === value}
              value={a.toString()}
            >
              {typeof description === "string"
                ? capitalize(description)
                : description}
            </option>
          )
        })}
      </select>
    </InputDiv>
  )
}
